/*
 * Adaah — wishlist.
 *
 * Saved on the shopper's device (localStorage): no app, no account, no sync between
 * devices. Not part of stock Horizon; loaded from layout/theme.liquid.
 *
 * Everything is driven from markup, so sections stay plain Liquid:
 *
 *   product-card                 gets a heart button added to its image, automatically,
 *                                including cards that carousels or filters load later
 *   [data-wishlist-handle]       any server-rendered heart button (blocks/wishlist-button.liquid
 *                                on the product page) gets its saved state and click handling
 *   #MainContent .buy-buttons-block
 *                                gets an "Add to wishlist" button after it, only on product
 *                                templates without the wishlist-button block
 *   [data-wishlist-count]        shows the number of saved items; hidden at 0
 *   [data-wishlist-grid]         renders the saved products (sections/wishlist-grid.liquid)
 *
 * Any change fires `adaah:wishlist-change` on document with { handles, count }.
 */
(() => {
    if (window.AdaahWishlist) return;

    const KEY = "adaah:wishlist";
    const EVENT = "adaah:wishlist-change";
    const MAX = 100;
    const ROOT = window.Shopify?.routes?.root || "/";

    const HEART =
        '<svg class="wl-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M12 20.25s-7.5-4.55-7.5-10.1A4.15 4.15 0 0 1 8.65 6c1.4 0 2.6.7 3.35 1.85A3.97 3.97 0 0 1 15.35 6a4.15 4.15 0 0 1 4.15 4.15c0 5.55-7.5 10.1-7.5 10.1Z"/>' +
        "</svg>";

    // --- Storage -------------------------------------------------------------------

    function read() {
        try {
            const value = JSON.parse(localStorage.getItem(KEY) || "[]");
            return Array.isArray(value) ? value.filter((h) => typeof h === "string" && h) : [];
        } catch {
            return [];
        }
    }

    function write(handles) {
        try {
            localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX)));
        } catch {
            // Private mode or storage full: the change still applies for this page view.
        }
        changed(handles);
    }

    function changed(handles) {
        document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles, count: handles.length } }));
    }

    const AdaahWishlist = {
        all: read,
        has: (handle) => read().includes(handle),
        add(handle) {
            const handles = read().filter((h) => h !== handle);
            write([handle, ...handles]);
        },
        remove(handle) {
            write(read().filter((h) => h !== handle));
        },
        toggle(handle) {
            this.has(handle) ? this.remove(handle) : this.add(handle);
        },
    };
    window.AdaahWishlist = AdaahWishlist;

    // --- Helpers ---------------------------------------------------------------------

    function handleFromUrl(href) {
        const match = /\/products\/([^/?#]+)/.exec(href || "");
        return match ? decodeURIComponent(match[1]) : null;
    }

    function heartButton(handle, title, extraClass) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = `wl-heart ${extraClass || ""}`.trim();
        button.dataset.wishlistHandle = handle;
        if (title) button.dataset.wishlistTitle = title;
        button.innerHTML = HEART;
        return button;
    }

    function syncButton(button, saved) {
        const title = button.dataset.wishlistTitle;
        button.setAttribute("aria-pressed", String(saved));
        const label = saved ? "Remove from wishlist" : "Add to wishlist";
        button.setAttribute("aria-label", title ? `${label}: ${title}` : label);
        const text = button.querySelector(".wl-heart__text");
        if (text) text.textContent = saved ? "Saved to wishlist" : "Add to wishlist";
    }

    function syncAll() {
        const handles = read();
        document.querySelectorAll("[data-wishlist-handle]").forEach((button) => {
            syncButton(button, handles.includes(button.dataset.wishlistHandle));
        });
        document.querySelectorAll("[data-wishlist-count]").forEach((badge) => {
            badge.textContent = handles.length > 99 ? "99+" : String(handles.length);
            badge.hidden = handles.length === 0;
        });
    }

    // --- Hearts on product cards and the product page ------------------------------

    function decorateCards(root) {
        let saved = null; // read storage once per pass, and only if a card needs a heart
        root.querySelectorAll("product-card").forEach((card) => {
            const gallery = card.querySelector(".card-gallery");
            if (!gallery || gallery.querySelector(":scope > .wl-heart")) return;

            const link = card.querySelector(".product-card__link") || card.querySelector('a[href*="/products/"]');
            const handle = handleFromUrl(link?.getAttribute("href"));
            if (!handle) return;

            const button = heartButton(handle, link.getAttribute("aria-label"), "wl-heart--card");
            gallery.append(button);
            saved ??= read();
            syncButton(button, saved.includes(handle));
        });
    }

    function decorateProductPage() {
        const handle = handleFromUrl(location.pathname);
        if (!handle) return;

        const buyButtons = document.querySelector("#MainContent .buy-buttons-block");
        // The wishlist-button block renders either style server-side; only build one if neither is there.
        if (!buyButtons || document.querySelector("#MainContent .wl-heart--product, #MainContent .wl-heart--title")) return;

        // Below "Order on WhatsApp" (blocks/whatsapp-order.liquid) when it is in the same column.
        // Not necessarily the next element: the buy-buttons block can output more after itself.
        const whatsapp = buyButtons.parentElement?.querySelector(":scope > .wa-order");

        const title = document.querySelector("#MainContent h1")?.textContent.trim();
        const button = heartButton(handle, title, "wl-heart--product");
        const text = document.createElement("span");
        text.className = "wl-heart__text";
        button.append(text);
        (whatsapp || buyButtons).after(button);
        syncButton(button, AdaahWishlist.has(handle));
    }

    // Capture phase on document runs before the product card's own click handler, so a
    // heart tap saves the product instead of opening it.
    document.addEventListener(
        "click",
        (event) => {
            const button = event.target instanceof Element && event.target.closest("[data-wishlist-handle]");
            if (!button) return;
            event.preventDefault();
            event.stopPropagation();
            AdaahWishlist.toggle(button.dataset.wishlistHandle);
        },
        { capture: true },
    );

    // --- Wishlist page grid --------------------------------------------------------

    function formatMoney(cents, format) {
        const value = (Number(cents) || 0) / 100;
        const group = (decimals, thousands, decimal) => {
            const [whole, fraction] = value.toFixed(decimals).split(".");
            const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
            return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };
        const plain = (format || "{{amount}}").replace(/<[^>]*>/g, "");
        return plain.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
            switch (key) {
                case "amount_no_decimals":
                    return group(0, ",", ".");
                case "amount_with_comma_separator":
                    return group(2, ".", ",");
                case "amount_no_decimals_with_comma_separator":
                    return group(0, ".", ",");
                case "amount_with_apostrophe_separator":
                    return group(2, "'", ".");
                default:
                    return group(2, ",", ".");
            }
        });
    }

    function sizedImage(src, width) {
        if (!src) return "";
        const url = src.startsWith("//") ? `https:${src}` : src;
        return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
    }

    function el(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text != null) node.textContent = text;
        return node;
    }

    function productCard(product, moneyFormat) {
        const item = el("li", "wl-card");
        const url = product.url || `${ROOT}products/${product.handle}`;

        const media = el("a", "wl-card__media");
        media.href = url;
        media.tabIndex = -1;
        media.setAttribute("aria-hidden", "true");
        if (product.featured_image) {
            const img = el("img");
            img.src = sizedImage(product.featured_image, 600);
            img.srcset = [300, 450, 600, 900].map((w) => `${sizedImage(product.featured_image, w)} ${w}w`).join(", ");
            img.sizes = "(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw";
            img.alt = "";
            img.loading = "lazy";
            media.append(img);
        }

        const heart = heartButton(product.handle, product.title, "wl-heart--card");
        syncButton(heart, true);

        const title = el("a", "wl-card__title", product.title);
        title.href = url;

        const price = el("p", "wl-card__price");
        const onSale = product.compare_at_price > product.price;
        const current = el("span", onSale ? "wl-card__price-sale" : "", formatMoney(product.price, moneyFormat));
        price.append(current);
        if (onSale) price.append(" ", el("s", "wl-card__price-compare", formatMoney(product.compare_at_price, moneyFormat)));
        if (!product.available) price.append(el("span", "wl-card__sold-out", "Sold out"));

        item.append(media, heart, title, price);
        return item;
    }

    async function renderGrid(grid) {
        const handles = read();
        const section = grid.closest("[data-wishlist-root]") || grid.parentElement;
        const empty = section.querySelector("[data-wishlist-empty]");
        const moneyFormat = grid.dataset.moneyFormat;

        const token = String(Date.now() + Math.random());
        grid.dataset.renderToken = token;

        if (handles.length === 0) {
            grid.replaceChildren();
            grid.hidden = true;
            if (empty) empty.hidden = false;
            return;
        }

        if (empty) empty.hidden = true;
        grid.hidden = false;
        grid.setAttribute("aria-busy", "true");

        const results = await Promise.all(
            handles.map((handle) =>
                fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: "application/json" } })
                    .then((response) => (response.ok ? response.json() : response.status === 404 ? "gone" : null))
                    .catch(() => null),
            ),
        );

        // A newer render started while this one was fetching.
        if (grid.dataset.renderToken !== token) return;
        grid.removeAttribute("aria-busy");

        // Products that were deleted or unpublished drop out of the list for good.
        const gone = handles.filter((_, i) => results[i] === "gone");
        if (gone.length) {
            const kept = read().filter((h) => !gone.includes(h));
            try {
                localStorage.setItem(KEY, JSON.stringify(kept));
            } catch {}
        }

        const products = results.filter((r) => r && r !== "gone");
        grid.replaceChildren(...products.map((product) => productCard(product, moneyFormat)));

        if (products.length === 0) {
            grid.hidden = true;
            if (empty) empty.hidden = false;
        }
        if (gone.length) syncAll();
    }

    function renderGrids() {
        document.querySelectorAll("[data-wishlist-grid]").forEach(renderGrid);
    }

    // --- Wiring --------------------------------------------------------------------

    let scheduled = false;
    function refresh() {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            decorateCards(document);
            decorateProductPage();
        });
    }

    document.addEventListener(EVENT, () => {
        syncAll();
        renderGrids();
    });

    // Another tab changed the list.
    window.addEventListener("storage", (event) => {
        if (event.key === KEY) changed(read());
    });

    // Back/forward cache restores a stale page.
    window.addEventListener("pageshow", (event) => {
        if (event.persisted) changed(read());
    });

    function start() {
        decorateCards(document);
        decorateProductPage();
        syncAll();
        renderGrids();
        // Carousels, filters, infinite scroll, recommendations and variant changes all
        // swap markup in after load; re-decorate whatever arrives.
        new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", start, { once: true });
    } else {
        start();
    }
})();
